import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useCreateDepartment } from "../../api/department/departmentQueries";
import { Button } from "../ui/button.tsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialogue.tsx";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";
import { Textarea } from "../ui/textarea.tsx";
import { LoadingSpinner } from "../ui/loading-spinner.tsx";
import { toast } from "react-toastify";

export function AddDepartmentModal({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const { mutate: createDepartment, isPending: isCreating } = useCreateDepartment();

  const validateForm = () => {
    const newErrors = {};
    
    if (!name || !name.trim()) {
      newErrors.name = 'Department name is required';
    }

    if (!description || !description.trim()) {
      newErrors.description = 'Department description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await createDepartment({
        name: name.trim(),
        description: description.trim()
      });
      setName("");
      setDescription("");
      setErrors({});
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Failed to create department");
    }
  };

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (!open) {
      setErrors({});
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Department</DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex justify-between">
              <span>Department Name</span>
              {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder="Enter department name"
              className={errors.name ? 'border-red-500' : ''}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="flex justify-between">
              <span>Description</span>
              {errors.description && <span className="text-sm text-red-500">{errors.description}</span>}
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: '' }));
              }}
              placeholder="Enter department description"
              rows={4}
              className={errors.description ? 'border-red-500' : ''}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? <LoadingSpinner className="mr-2" /> : null}
              Add Department
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}